import React from 'react';
import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { AuthAPI } from '../../../api';
import { AuthStore } from '../../../store';
import FormControl from '@material-ui/core/FormControl';
import { validateEmail, validateUsername, validatePassword } from './Validate';
import Button from '../../../components/Button';
import { TextInput } from './Inputs';
import PasswordDialog from './PasswordDialog';
import AlertList from './AlertList';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Typography from '@material-ui/core/Typography';
import {useSelector, useDispatch} from 'react-redux';
import * as track from '../../../lib/track';


const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: '1rem 0',
  },
  control: {
    width: '25rem',
    marginBottom: '2rem',
  },
  label: {
    fontSize: '1.3rem',
    marginBottom: '0.7rem',
    color: theme.palette.text.primary,
    '&:focus': {
      color: theme.palette.text.primary,
    },
  },
  row: {
    width: '25rem',
    marginBottom: '1rem',
  },
}));

const UserAccountForm = ({isAdmin=false, setProfile=null}) => {
  const classes = useStyles();
  const [alerts, setAlerts] = useState([]);
  const profile = useSelector(state => state.auth.profile);
  const currentUuid = useSelector(state => state.auth.profile.user_uuid);
  const socialLogin = useSelector(state => !!state.auth.socialLogin);
  const dispatch = useDispatch();
  const [username, setUsername] = useState(profile.username);
  const [usernameError, setUsernameError] = useState();
  const [email, setEmail] = useState(profile.email);
  const [emailError, setEmailError] = useState();
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState();
  const [busy, setBusy] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const disabled =
    usernameError !== undefined ||
    emailError !== undefined ||
    passwordError !== undefined ||
    busy;
  const apiUpdateProfile = isAdmin ? AuthAPI.adminUpdateProfile : AuthAPI.updateProfile;
  const passwordLocked = !isAdmin && socialLogin;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // if the profile gets updated, update the form.
  useEffect(() => setEmail(profile.email), [profile.email]);
  useEffect(() => setUsername(profile.username), [profile.username]);

  const addAlerts = (newAlerts) => {
    setAlerts(alerts => [...newAlerts, ...alerts]);
  };

  const removeAlert = (alert) => {
    setAlerts(alerts => alerts.filter(a => a !== alert));
  };

  const emailValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setEmail(value);
    if (!changing || emailError) setEmailError(validateEmail(value));
  };

  const usernameValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setUsername(value);
    if (!changing || usernameError) setUsernameError(validateUsername(value));
  };

  const passwordValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setPassword(value);
    if (!changing || passwordError) setPasswordError(value === '' ? undefined : validatePassword(value));
  };

  const onSave = async (currentPassword=undefined) => {

    track.sendEvent('save account credentials', 'click', {});

    // if the password is changing, prompt for old password.
    if (password !== '' && currentPassword === undefined && !isAdmin) {
      setPasswordDialogOpen(true);
      return;
    }

    // compose the arguments to be sent
    const updates = {};
    if (email !== profile.email) {
      updates.email = email;
    }
    if (password !== '') {
      updates.new_password = password;
      updates.password = currentPassword;
    }
    if (username !== profile.username) {
      updates.username = username;
    }

    setAlerts([]);

    // if there is nothing to update, say so and abort
    if (Object.keys(updates).length === 0) {
      addAlerts([{severity: 'info', text: 'No changes to save.'}]);
      return;
    }

    // send update to the API
    setBusy(true);
    updates.uuid = profile.user_uuid;  // required for admin updates, ignored otherwise
    try {
      const response = await apiUpdateProfile(updates);
      addAlerts(response.payload.messages);
      if (profile.user_uuid === currentUuid) {
        // if isAdmin, may or may not be profile of current user.
        dispatch(AuthStore.profile(response.payload.profile));
      }
      if (setProfile) {
        setProfile(response.payload.profile);
      }
      setPassword('');
    } catch (err) {
      if (err.body) {
        const { message } = err.body;
        addAlerts([{severity: 'error', text: message}]);
      } else {
        addAlerts([{severity: 'error', text: 'An error occurred updating your profile.'}]);
      }
    } finally {
      setBusy(false);
    }

  };

  return (
    <div className={classes.root}>
      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Username
        </Typography>
        <Typography variant="body2" gutterBottom>
          Your username will appear on any reviews you post so please <b>do not use your email address</b> as your username.
        </Typography>
        <Typography variant="body2" gutterBottom>
          Your username should only contain letter&apos;s numbers and dash (&apos;-&apos;) or underscore (&apos;_&apos;) characters.
        </Typography>
        {usernameError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {usernameError}
          </Typography>
        ) : null}
        <TextInput
          id="username-input"
          autoComplete="username"
          value={username}
          variant="outlined"
          placeholder="Username"
          error={usernameError !== undefined}
          onChange={usernameValidator(true)}
          onBlur={usernameValidator(false)}
        />
      </FormControl>
      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Email address
        </Typography>
        {emailError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {emailError}
          </Typography>
        ) : null}
        <TextInput
          id="email-input"
          autoComplete="email"
          value={email}
          variant="outlined"
          placeholder="Email address"
          error={emailError !== undefined}
          onChange={emailValidator(true)}
          onBlur={emailValidator(false)}
        />
      </FormControl>
      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Change password
        </Typography>
        <Typography variant="body2" gutterBottom>
          Your password must contain at least one upper case letter, lower case letter, and a number. You will be required to enter your current password in the next step to confirm the change.
        </Typography>
        {passwordError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {passwordError}
          </Typography>
        ) : null}
        <TextInput
          id="password-input"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          variant="outlined"
          placeholder={passwordLocked ? 'Cannot be changed with social login.' : 'New password'}
          error={passwordError !== undefined}
          onChange={passwordValidator(true)}
          onBlur={passwordValidator(false)}
          disabled={passwordLocked}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
      <div className={classes.row}>
        <AlertList alerts={alerts} onClose={removeAlert} />
      </div>
      <div className={classes.row}>
        <Button
          label="Save"
          color="primary"
          pointer="none"
          onClick={()=>onSave()}
          disabled={disabled}
          busy={busy}
          style={{width: '10rem'}}
        />
      </div>
      <PasswordDialog
        open={passwordDialogOpen}
        onCancel={()=>setPasswordDialogOpen(false)}
        onSubmit={(password)=>{setPasswordDialogOpen(false); onSave(password);}}
      />
    </div>
  );
};

UserAccountForm.propTypes = {
  isAdmin: PropTypes.bool,
  setProfile: PropTypes.func,
};

export default UserAccountForm;
